import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Dialog, DialogTitle, TextField } from "@mui/material";
import { Order, stableSort, getComparator } from "../ui/Tables";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { useEffect, useState } from "react";
import { Loading } from "../ui/Loading";
import { uiStore } from "../uiStore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { RisorseSpecifiche, RisorseTipologie } from "../prismaTypes";
import { EditTipologia } from "./EditTipologia";

interface HeadCell {
  disablePadding: boolean;
  id: keyof RisorseTipologie;
  label: string;
  numeric: boolean;
  key: string;
  filter: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    numeric: true,
    disablePadding: false,
    label: "",
    key: "id",
    filter: false,
  },
  {
    id: "definizione",
    numeric: false,
    disablePadding: false,
    label: "Definizione",
    key: "definizione",
    filter: true,
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof RisorseTipologie) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  onFilter: (event: any, property: keyof RisorseTipologie, filter: string | number) => void;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, onFilter } = props;
  const createSortHandler =
    (property: keyof RisorseTipologie) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const createFilterHandler = (property: keyof RisorseTipologie) => (event: any) => {
    onFilter(event, property, event.target.value);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.key}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.key}>
            {headCell.filter && (
              <TextField label="" variant="standard" onChange={createFilterHandler(headCell.id)} />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function Tipologie() {
  const { isLoading, data } = useQuery<
    { tipologie: RisorseTipologie[]; specifiche: RisorseSpecifiche[] },
    AxiosError
  >(["selectTipSpec"], () => axios.get(API_URL + "risorse/selectTipSpec").then((res) => res.data));

  const rows = data?.tipologie ?? [];

  const [tipologia, setTipologia] = useState<RisorseTipologie | undefined>();
  const [open, setOpen] = useState(false);
  const [openNew, setOpenNew] = useState(false);

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof RisorseTipologie>("definizione");
  const [page, setPage] = React.useState(0);
  const dense = oraPrenotaStore((state) => state.tabelleCompatte);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof RisorseTipologie
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [filters, setFilters] = useState({});
  const handleFilterRequest = (
    event: React.MouseEvent<unknown>,
    property: keyof RisorseTipologie,
    filter: string | number
  ) => {
    setFilters({
      ...filters,
      [property]: filter,
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => {
    setTitle("Tipologie");
  }, [setTitle]);

  const handleClick = (conto: RisorseTipologie) => () => {
    setTipologia(conto);
    setOpen(true);
  };

  const handleOnpNew = () => {
    setOpenNew(true);
  };

  if (isLoading) return <Loading />;

  return (
    <Box sx={{ width: "100%" }}>
      <Button
        variant="text"
        color="secondary"
        startIcon={<AddCircleOutlineIcon />}
        onClick={handleOnpNew}
      >
        Aggiungi Tipologia
      </Button>
      <TableContainer>
        <Table aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            onFilter={handleFilterRequest}
          />
          <TableBody classes={{ root: "table-body" }}>
            {stableSort(rows, getComparator(order, orderBy), filters)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: RisorseTipologie, index) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>
                      <IconButton sx={{ p: 0 }} onClick={handleClick(row)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>

                    <TableCell>{row.definizione}</TableCell>
                  </TableRow>
                );
              })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        {tipologia && (
          <>
            <DialogTitle>AA{tipologia.definizione}</DialogTitle>
            <EditTipologia tipologia={tipologia} handleClose={() => setOpen(false)} />
          </>
        )}
      </Dialog>

      <Dialog open={openNew} onClose={() => setOpenNew(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nuova Specifica</DialogTitle>
        <EditTipologia handleClose={() => setOpenNew(false)} />
      </Dialog>
    </Box>
  );
}
